/**
 * One number under the hero, on every supporting page.
 *
 * ⚠️ IT EXISTS BECAUSE THE PAGES WERE SHOWING A ZERO THEY DID NOT MEAN (Josh,
 * 2026-09-04: "it shows zero on just about each thing but after about 1 or 2
 * seconds, the numbers all update like a jolt of lightning"). Every page
 * computed its stats from `rows ?? []`, so before the fetch landed the cards
 * confidently read 0 — and "0 accounts", "0 awaiting a claim number" are
 * statements a member can act on. The jolt was the visible symptom; the real
 * fault was answering a question we had not yet asked the server.
 *
 * ⚠️ SO IT SKELETONS, IT DOES NOT COUNT UP. The obvious "smooth" fix is to
 * animate 0 → 6, but that spends a second animating through numbers that were
 * never true, and on a card reading "1 awaiting a claim number" a count-up is
 * both slower and sillier than the truth. A placeholder says "not known yet",
 * which is what is actually the case, and the value fades in when it is known.
 *
 * ⚠️ AND IT IS A COMPONENT, NOT A CLASS TO REMEMBER. The same twelve lines of
 * markup were copied across five pages; the loading rule would have been five
 * copies too, and PageHero already taught us how that ends — applied by hand
 * across six files, wrong on two of them, all six compiling.
 */
import type { ReactNode } from 'react';

export type StatTone = 'blue' | 'green' | 'amber' | 'violet' | 'slate';

export function StatCard({
  tone,
  icon,
  label,
  value,
  note,
  loading = false,
  /** Amber border: something here needs attention. Never decorative. */
  slow = false,
  /**
   * Red border: past the point where it is merely slow.
   *
   * ⚠️ TAKES PRECEDENCE OVER `slow`, because a caller computing both from
   * thresholds will often set both — Loss Runs marks 7 days slow and 14 late,
   * and 14 is also greater than 7.
   */
  late = false,
  unit,
}: {
  tone: StatTone;
  /** SVG path data for a 24×24 viewBox. */
  icon: string;
  label: string;
  /** The number itself. Ignored while `loading`. */
  value: ReactNode;
  note: ReactNode;
  loading?: boolean;
  slow?: boolean;
  late?: boolean;
  unit?: string;
}) {
  return (
    <div className={`stat-card${loading ? '' : late ? ' is-late' : slow ? ' is-slow' : ''}`}>
      <span className={`stat-ic is-${tone}`}>
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d={icon} />
        </svg>
      </span>
      <div>
        <span className="stat-label">{label}</span>
        {loading ? (
          <>
            {/*
              aria-hidden on the bars, and the real state announced once. A
              screen reader hitting four shimmering divs would otherwise be told
              nothing at all about why the number is missing.
            */}
            <b className="stat-num" aria-hidden="true">
              <span className="stat-skel" />
            </b>
            <span className="stat-note">
              <span className="stat-skel is-thin" aria-hidden="true" />
              <span className="sr-only">Loading {label}</span>
            </span>
          </>
        ) : (
          <>
            <b className="stat-num is-in">
              {value}
              {unit && <em>{unit}</em>}
            </b>
            <span className="stat-note is-in">{note}</span>
          </>
        )}
      </div>
    </div>
  );
}
